
export const extractStringValue = (jsonString: string, fieldName: string): string | null => {
  const fieldRegex = new RegExp(`"${fieldName}"\\s*:\\s*"`, 'g');
  const match = fieldRegex.exec(jsonString);
  if (!match) return null;

  const startIndex = match.index + match[0].length;
  let i = startIndex;
  let escaped = false;

  while (i < jsonString.length) {
    const char = jsonString[i];
    if (escaped) {
      escaped = false;
      i++;
      continue;
    }
    if (char === '\\') {
      escaped = true;
      i++;
      continue;
    }
    if (char === '"') {
      // Found the closing quote, extract the value
      try {
        const valueString = jsonString.substring(startIndex - 1, i + 1); // Include quotes for JSON.parse
        return JSON.parse(valueString);
      } catch {
        return null;
      }
    }
    i++;
  }

  return null; // String not complete yet
};

// Helper function to extract array items from a potentially incomplete array of strings
export const extractArrayItems = (jsonString: string, fieldName: string): string[] => {
  const fieldRegex = new RegExp(`"${fieldName}"\\s*:\\s*\\[`, 'g');
  const match = fieldRegex.exec(jsonString);
  if (!match) return [];

  const startIndex = match.index + match[0].length;
  const remaining = jsonString.substring(startIndex);

  const items: string[] = [];
  let i = 0;
  let inString = false;
  let escaped = false;
  let currentItemStart = -1;

  while (i < remaining.length) {
    const char = remaining[i];

    if (escaped) {
      escaped = false;
      i++;
      continue;
    }

    if (char === '\\') {
      escaped = true;
      i++;
      continue;
    }

    if (char === '"') {
      if (!inString) {
        // Start of a new string item
        currentItemStart = i;
        inString = true;
      } else {
        // End of current string item
        try {
          const itemString = remaining.substring(currentItemStart, i + 1);
          const item = JSON.parse(itemString);
          items.push(item);
          currentItemStart = -1;
        } catch {
          // Item might be incomplete, skip it
        }
        inString = false;
      }
      i++;
      continue;
    }

    if (inString) {
      i++;
      continue;
    }

    // Outside of strings, check for array end or comma
    if (char === ']') {
      // End of array
      break;
    }

    if (char === ',') {
      // Comma separator, continue to next item
      i++;
      continue;
    }

    i++;
  }

  return items;
};

// Helper function to extract numeric value from a potentially incomplete JSON
export const extractNumericValue = (jsonString: string, fieldName: string): number | null => {
  const fieldRegex = new RegExp(`"${fieldName}"\\s*:\\s*`, 'g');
  const match = fieldRegex.exec(jsonString);
  if (!match) return null;

  const startIndex = match.index + match[0].length;
  let i = startIndex;
  let valueString = '';

  // Skip whitespace
  while (i < jsonString.length && /\s/.test(jsonString[i])) {
    i++;
  }

  // Extract the numeric value (could be integer or float)
  while (i < jsonString.length) {
    const char = jsonString[i];
    if (char === ',' || char === '}' || char === ']' || /\s/.test(char)) {
      // End of value
      break;
    }
    valueString += char;
    i++;
  }

  if (valueString.length === 0) {
    return null;
  }

  // Try to parse the numeric value
  const numValue = Number(valueString);
  if (!isNaN(numValue)) {
    return numValue;
  }

  return null;
};

export const looksLikeJSON = (str: string): boolean => {
  const trimmed = str.trim();
  return (
    trimmed.startsWith('{') ||
    trimmed.startsWith('[') ||
    trimmed.includes('{') ||
    trimmed.includes('[')
  );
};

const extractFirstJSONObject = (input: string): string | null => {
  const start = input.indexOf("{");
  if (start === -1) {
    return null;
  }

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < input.length; i++) {
    const ch = input[i];

    if (escaped) {
      escaped = false;
      continue;
    }

    if (ch === "\\") {
      escaped = true;
      continue;
    }

    if (ch === "\"") {
      inString = !inString;
      continue;
    }

    if (inString) {
      continue;
    }

    if (ch === "{") {
      depth++;
      continue;
    }

    if (ch === "}") {
      depth--;
      if (depth === 0) {
        return input.slice(start, i + 1);
      }
    }
  }

  // Incomplete JSON object: return from first "{" onward for partial parsing.
  return input.slice(start);
};

export const parsePartialJSON = <T>(jsonString: string, extractPartialFields: (jsonString: string) => Partial<T> | null): Partial<T> | null => {
  if (!jsonString || jsonString.trim().length === 0) {
    return null;
  }

  const target = extractFirstJSONObject(jsonString) || jsonString;

  if (!looksLikeJSON(target)) {
    return null;
  }

  // Try to parse complete JSON first
  try {
    return JSON.parse(target) as T;
  } catch {
    // If parsing fails, try to extract partial fields using the provided function
    const partial = extractPartialFields(target);
    return partial && Object.keys(partial).length > 0 ? partial : null;
  }
};

